import { Match } from "model/Match/Match";
import { MatchmakingTrack } from "model/Match/Set/Matchmaking/Track/MatchmakingTrack";
import { getResults } from "model/Results";

export type MultiMmSeasonRecord = {
  rosterId: string;
  matchesPlayed: number;
  wins: number;
  draws: number;
  loses: number;
  roundPointsFor: number;
  roundPointsAgainst: number;
  setPointsFor: number;
  setPointsAgainst: number;
  points: number;
  position: number | undefined;
};

export type MultiMmSeasonRecordWithDisplay = {
  displayName: string | undefined;
  orgId: string | undefined;
  record: MultiMmSeasonRecord;
};

const createMultiMmSeasonRecord = (rosterId: string): MultiMmSeasonRecord => {
  return {
    rosterId,
    matchesPlayed: 0,
    wins: 0,
    draws: 0,
    loses: 0,
    roundPointsFor: 0,
    roundPointsAgainst: 0,
    setPointsFor: 0,
    setPointsAgainst: 0,
    points: 0,
    position: undefined,
  };
};

export const calculateMultiMmSeasonRecords = (
  rosterIds: string[],
  matches: Match[]
) => {
  const records = new Map<string, MultiMmSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createMultiMmSeasonRecord(id));
  });

  for (var matchI = 0; matchI < matches.length; matchI++) {
    const match = matches[matchI];
    const results = getResults(match.finalPositions, 1);
    if (!results) {
      continue;
    }

    for (var rosterI = 0; rosterI < rosterIds.length; rosterI++) {
      const rosterId = rosterIds[rosterI];
      const rosterRecord = records.get(rosterId);
      const result = results[rosterId];
      if (!rosterRecord || !result) {
        continue;
      }

      switch (result) {
        case "W":
          rosterRecord.wins++;
          rosterRecord.points += 3;
          break;
        case "D":
          rosterRecord.draws++;
          rosterRecord.points += 1;
          break;
        case "L":
          rosterRecord.loses++;
          break;
      }

      const setPoints = match.scores ?? {};
      const otherId = Object.keys(setPoints).find((id) => id !== rosterId)!;
      rosterRecord.setPointsFor += setPoints[rosterId] ?? 0;
      rosterRecord.setPointsAgainst += setPoints[otherId] ?? 0;

      match.sets.forEach((s) => {
        s.tracks.forEach((t) => {
          const trackScores = (t as MatchmakingTrack).scores ?? {};
          rosterRecord.roundPointsFor += trackScores[rosterId] ?? 0;
          rosterRecord.roundPointsAgainst += trackScores[otherId] ?? 0;
        });
      });
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0) {
    return [];
  }

  sortedRecords[0].position = 1;

  for (let r = 1; r < sortedRecords.length; r++) {
    const record = sortedRecords[r];
    if (compareRecords(record, sortedRecords[r - 1]) === 0) {
      record.position = sortedRecords[r - 1].position;
    } else {
      record.position = r + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (
  a: MultiMmSeasonRecord,
  b: MultiMmSeasonRecord
): number => {
  if (a.points > b.points) {
    return -1;
  }

  if (b.points > a.points) {
    return 1;
  }

  const aSetDiff = a.setPointsFor - a.setPointsAgainst;
  const bSetDiff = a.setPointsFor - a.setPointsAgainst;
  if (aSetDiff > bSetDiff) {
    return -1;
  }

  if (bSetDiff > aSetDiff) {
    return 1;
  }

  const aRoundDiff = a.roundPointsFor - a.roundPointsAgainst;
  const bRoundDiff = b.roundPointsFor - b.roundPointsAgainst;
  if (aRoundDiff > bRoundDiff) {
    return -1;
  }

  if (bRoundDiff > aRoundDiff) {
    return 1;
  }

  if (a.roundPointsFor > b.roundPointsFor) {
    return -1;
  }

  if (b.roundPointsFor > a.roundPointsFor) {
    return 1;
  }

  return 0;
};
